import { FC, ReactElement } from 'react'

import styled from 'styled-components'
import { useDisclosure } from '@mantine/hooks'
import { Badge } from '@mantine/core'

import DefaultDocument from 'src/Assets/document.svg?react'
import Sign from 'src/Assets/sign.svg?react'
import Signature from 'src/Assets/signature.svg?react'
import Trash from 'src/Assets/trash.svg?react'
import { Contract, ContractStatus } from 'src/Redux/api/contractsApi.ts'
import {
  DocumentStatusEnum,
  ApprovalStatusEnum,
} from 'src/Redux/api/documentsApi.ts'
import { colors, device } from 'src/Helpers/constants.ts'
import { IconButton } from 'src/Components/Button/IconButton.tsx'
import { SignContractModal } from 'src/Pages/ContractsPage/SignContractModal.tsx'
import { SendForApprovalModal } from 'src/Components/SendForApprovalModal'
import { User } from 'src/Redux/api/usersApi'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${colors.mainGray};
  padding: 10px;
  gap: 8px;
`

const ParametersContainer = styled.div`
  display: grid;
  grid-column-gap: 16px;
  grid-template-columns: 0.6fr 0.4fr 0.4fr 0.4fr 0.6fr 1fr;

  @media ${device.tabletMax} {
    grid-template-columns: 1fr;
    row-gap: 12px;
  }
`
const Parameter = styled.span`
  font-size: 12px;
  max-width: 300px;
  flex-grow: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  color: ${colors.thirdGray};
  font-weight: 600;
  justify-content: center;
  flex-shrink: 0;
`

const NameWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 20px;

  @media ${device.tabletMax} {
    gap: 12px;
  }
`

const Name = styled.span`
  font-size: 14px;
  max-width: 200px;
  width: 100%;
  text-align: left;
  color: ${colors.thirdGray};
  font-weight: 600;
  text-align: center;

  @media ${device.tabletMax} {
    max-width: 100%;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;

  @media ${device.tabletMax} {
    justify-content: flex-end;
  }
`

const StyledButton = styled(IconButton)`
  background-color: ${colors.fourthBlue} !important;

  &:hover {
    background-color: ${colors.fourthBlue} !important;
  }
`

const StatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const ApproverText = styled.span`
  font-size: 11px;
  color: ${colors.thirdGray};
  opacity: 0.8;
`

const CommentColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 11px;
  color: ${colors.thirdGray};
  justify-content: center;
  align-items: flex-start;
  text-align: left;
`

const CommentText = styled.span`
  font-size: 12px;
  color: ${colors.thirdGray};
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const CommentAuthor = styled.span`
  font-size: 10px;
  color: ${colors.thirdGray};
  opacity: 0.7;
`

interface ContractItemProps {
  contract: Contract
  status: ContractStatus
  users: User[]
  currentUserId?: number
  canArchive?: boolean
  onArchive?: (id: number) => void
}

export const ContractItem: FC<ContractItemProps> = ({
  contract,
  status,
  users,
  currentUserId,
  canArchive,
  onArchive,
}) => {
  const [isAddModalOpened, { open: openAddModal, close: closeAddModal }] =
    useDisclosure(false)
  const [
    isApprovalModalOpened,
    { open: openApprovalModal, close: closeApprovalModal },
  ] = useDisclosure(false)

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    return date.toLocaleDateString('ru-RU')
  }

  const getLastComment = (): {
    comment: string
    userName: string
  } | null => {
    const approvalHistory = contract.document?.approvalHistory
    if (!approvalHistory) return null

    const itemWithComment = approvalHistory.find(
      (item) =>
        item.comment &&
        (item.status === ApprovalStatusEnum.APPROVED ||
          item.status === ApprovalStatusEnum.REJECTED),
    )

    if (!itemWithComment || !itemWithComment.comment) return null

    const userName = itemWithComment.user
      ? itemWithComment.user.firstName || itemWithComment.user.lastName
        ? [itemWithComment.user.firstName, itemWithComment.user.lastName]
            .filter(Boolean)
            .join(' ')
        : itemWithComment.user.email
      : 'Неизвестный'

    return {
      comment: itemWithComment.comment,
      userName,
    }
  }

  const lastComment = getLastComment()

  const onSignContract = (): void => {
    openAddModal()
  }

  const onCloseSignContract = (): void => {
    closeAddModal()
  }

  const getCurrentApprover = (): string | null => {
    const approvalHistory = contract.document?.approvalHistory
    if (!approvalHistory || approvalHistory.length === 0) return null

    let targetApproval = null

    if (contract.document?.status === DocumentStatusEnum.ON_APPROVAL) {
      // Для статуса "На согласовании" - ищем текущего согласователя
      targetApproval = approvalHistory.find(
        (item) => item.status === ApprovalStatusEnum.IN_PROCESS,
      )
    } else if (contract.document?.status === DocumentStatusEnum.REJECTED) {
      // Для статуса "Отклонено" - ищем того, кто отклонил
      targetApproval = approvalHistory.find(
        (item) => item.status === ApprovalStatusEnum.REJECTED,
      )
    }

    if (!targetApproval?.user) return null

    const { firstName, lastName, email } = targetApproval.user
    if (firstName || lastName) {
      return [firstName, lastName].filter(Boolean).join(' ')
    }
    return email
  }

  const getDocumentStatusBadge = (): ReactElement | null => {
    if (!contract.document) return null

    const statusColors: Record<DocumentStatusEnum, string> = {
      [DocumentStatusEnum.DRAFT]: 'gray',
      [DocumentStatusEnum.ON_APPROVAL]: 'blue',
      [DocumentStatusEnum.APPROVED]: 'green',
      [DocumentStatusEnum.REJECTED]: 'red',
    }

    const statusLabels: Record<DocumentStatusEnum, string> = {
      [DocumentStatusEnum.DRAFT]: 'Черновик',
      [DocumentStatusEnum.ON_APPROVAL]: 'На согласовании',
      [DocumentStatusEnum.APPROVED]: 'Согласовано',
      [DocumentStatusEnum.REJECTED]: 'Отклонено',
    }

    return (
      <Badge color={statusColors[contract.document.status]}>
        {statusLabels[contract.document.status]}
      </Badge>
    )
  }

  // Показываем кнопку отправки на согласование если:
  // 1. Документ ещё не создан (черновик)
  // 2. Документ был отклонён
  const shouldShowSendForApproval =
    (!contract.documentId && status === ContractStatus.Draft) ||
    contract.document?.status === DocumentStatusEnum.REJECTED
  const shouldShowSign = status === ContractStatus.OnSign

  return (
    <Container>
      <SignContractModal
        isOpened={isAddModalOpened}
        onAddClient={onSignContract}
        onClose={onCloseSignContract}
      />
      <SendForApprovalModal
        opened={isApprovalModalOpened}
        onClose={closeApprovalModal}
        contractId={contract.id}
        users={users}
        currentUserId={currentUserId}
      />
      <ParametersContainer>
        <NameWrapper>
          <DefaultDocument width={50} height={50} />
          {contract.googleDocsUrl ? (
            <a
              href={contract.googleDocsUrl}
              target='_blank'
              rel='noopener noreferrer'
            >
              <Name>{contract.client.name}</Name>
            </a>
          ) : (
            <Name>{contract.client.name}</Name>
          )}
        </NameWrapper>

        <Parameter>{contract.contractNumber || 'Без номера'}</Parameter>

        <Parameter>
          <span>{formatDate(contract.createdAt)}</span>
        </Parameter>

        <Parameter>
          <StatusContainer>
            {getDocumentStatusBadge() || status}
            {getCurrentApprover() && (
              <ApproverText>{getCurrentApprover()}</ApproverText>
            )}
          </StatusContainer>
        </Parameter>

        <CommentColumn>
          {lastComment ? (
            <>
              <CommentText>{lastComment.comment}</CommentText>
              <CommentAuthor>{lastComment.userName}</CommentAuthor>
            </>
          ) : (
            <span style={{ opacity: 0.5 }}>—</span>
          )}
        </CommentColumn>

        <ButtonContainer>
          {shouldShowSign && (
            <StyledButton
              onClick={() => onSignContract()}
              tooltiptext={'Подписать'}
            >
              <Signature width={25} height={25} />
            </StyledButton>
          )}

          {shouldShowSendForApproval && (
            <StyledButton
              onClick={() => openApprovalModal()}
              tooltiptext={'Отправить на согласование'}
            >
              <Sign width={25} height={25} />
            </StyledButton>
          )}

          {canArchive && (
            <StyledButton
              onClick={() => onArchive?.(contract.id)}
              tooltiptext={'В архив'}
              data-testid='archive-contract-button'
            >
              <Trash width={25} height={25} />
            </StyledButton>
          )}
        </ButtonContainer>
      </ParametersContainer>
    </Container>
  )
}
